import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { translate } from 'react-i18next';
import Spin from 'antd/lib/spin';
import Button from 'antd/lib/button';
import { selectIsInProgress } from '../../../features/spinner/ducks';
import { selectToken, selectIsAdmin } from '../../../features/auth/ducks';
import accountApi from '../../account-management/api';
import {
  selectLoan,
  fetchLoanActions,
  acceptLoanActions,
  rejectLoanActions,
  selectEarlyRepayment,
  fetchEarlyRepaymentActions,
  earlyRepayLoanActions,
  closeEarlyRepayLoanModal,
  fetchEarlyRepayRecordActions,
  selectEarlyRepaymentRecord,
  shiftInstallmentDateActions,
  cancelLoanActions,
} from '../ducks';
import {
  fetchPenaltyFeesByLoanIdAction,
  selectPenaltyFees,
  stornoPenaltyFeeRequest
} from '../../penalty-fee/ducks';
import { apiCallIds } from '../api';
import { LoanDetail } from '../components';
import { fetchLoanAllocationsActions, selectLoanAllocations } from '../../allocation-management/ducks';

// PSD2
import Psd2StatementsAdmin from '../../account-management/components/psd2/Psd2StatementsAdmin';
import {
  fetchPsd2RequisitionActions,
  fetchPsd2AccountDataActions,
  fetchPsd2IbansActions,
  fetchPsd2AllTxActions,
  fetchPsd2CategoriesActions,
  fetchPsd2AggregationCriteriaActions,
  purgePsd2DataActions,
  clearPsd2RequisitionAccounts,
  selectPsd2RequisitionAccountIds,
  selectPsd2AccountData,
  selectPsd2Ibans,
  selectPsd2Transactions,
  selectPsd2Categories,
  selectPsd2AggregationCriteria,
} from '../../../features/psd2/ducks';
import { selectSetting } from '../../../features/settings/ducks';

const mapStateToProps = (state, props) => ({
  loan: selectLoan(state),
  loanAllocations: selectLoanAllocations(state),
  isLoading:
    selectIsInProgress(state, apiCallIds.FETCH_LOAN) ||
    selectIsInProgress(state, apiCallIds.DISPOSE_LOAN),
  isAccepting: selectIsInProgress(state, apiCallIds.ACCEPT_LOAN),
  isCancelling: selectIsInProgress(state, apiCallIds.CANCEL_LOAN),
  loanId: props.params.loanId,
  token: selectToken(state),
  isAdmin: selectIsAdmin(state),
  items: selectPenaltyFees(state),
  earlyRepayment: selectEarlyRepayment(state),
  earlyRepaymentRecord: selectEarlyRepaymentRecord(state),

  // PSD2
  usePSD2Integration: selectSetting(state, 'PSD2_INTEGRATION_ON'),
  psd2AccountIds: selectPsd2RequisitionAccountIds(state) || [],
  psd2AccountData: selectPsd2AccountData(state) || null,
  psd2Ibans: selectPsd2Ibans(state),
  psd2Transactions: selectPsd2Transactions(state) || [],
  psd2Categories: selectPsd2Categories(state) || [],
  psd2Criteria: selectPsd2AggregationCriteria(state) || [],
});

const mapDispatchToProps = dispatch => ({
  resetEarlyRepayment: () => dispatch(closeEarlyRepayLoanModal()),
  actions: bindActionCreators(
    {
      fetchLoan: fetchLoanActions.request,
      acceptLoan: acceptLoanActions.request,
      rejectLoan: rejectLoanActions.request,
      fetchEarlyRepaymentAmount: fetchEarlyRepaymentActions.request,
      earlyRepayLoan: earlyRepayLoanActions.request,
      fetchLoanAllocations: fetchLoanAllocationsActions.request,
      fetchPenaltyFeesByLoanId: fetchPenaltyFeesByLoanIdAction.request,
      stornoPenaltyFee: stornoPenaltyFeeRequest,
      fetchEarlyRepayRecord: fetchEarlyRepayRecordActions.request,
      shiftInstallmentDate: shiftInstallmentDateActions.request,
      cancelLoan: cancelLoanActions.request,

      // PSD2
      psd2FetchRequisition: fetchPsd2RequisitionActions.request,
      psd2FetchAccountData: fetchPsd2AccountDataActions.request,
      psd2FetchIbans: fetchPsd2IbansActions.request,
      psd2FetchAllTx: fetchPsd2AllTxActions.request,
      psd2FetchCategories: fetchPsd2CategoriesActions.request,
      psd2FetchCriteria: fetchPsd2AggregationCriteriaActions.request,
      psd2PurgeData: purgePsd2DataActions.request,
      psd2ClearAccounts: clearPsd2RequisitionAccounts,
    },
    dispatch
  ),
});

@translate()
@connect(mapStateToProps, mapDispatchToProps)
export default class LoanDetailContainer extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    loan: PropTypes.object,
    loanAllocations: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
    isAccepting: PropTypes.bool.isRequired,
    isCancelling: PropTypes.bool.isRequired,
    loanId: PropTypes.string.isRequired,
    actions: PropTypes.object.isRequired,
    earlyRepayment: PropTypes.object,
    earlyRepaymentRecord: PropTypes.object,
    resetEarlyRepayment: PropTypes.func.isRequired,
    items: PropTypes.array,
    isAdmin: PropTypes.bool.isRequired,

    // PSD2
    psd2AccountIds: PropTypes.array.isRequired,
    psd2AccountData: PropTypes.object,
    psd2Ibans: PropTypes.array.isRequired, // [{accountId, IBAN}]
    psd2Transactions: PropTypes.array.isRequired,
    psd2Categories: PropTypes.array.isRequired,
    psd2Criteria: PropTypes.array.isRequired,
  };

  state = {
    showPsd2: false,
    selectedPsd2AccountId: null,
    accountDevices: [],
    sameInstallAccountsCount: 0,
  };

  componentDidMount() {
    const loanId = this.props.loanId;
    const actions = this.props.actions;
    actions.fetchLoan({ loanId });
    actions.fetchEarlyRepayRecord({ loanId });
    actions.fetchLoanAllocations({ loanId });
    actions.fetchPenaltyFeesByLoanId(loanId);
    if (this.props.loan) this.fetchAccountDevices(this.props.loan.accountId);
  }

  getRequisitionIdsForLoan = () => {
    const loan = this.props.loan || {};
    const ids = new Set();

    if (Array.isArray(loan.psd2Requisitions)) {
      loan.psd2Requisitions.filter((r)=>(r.system_status !== 'deleted') && (r.status === 'LN' )).forEach(r => r && r.id && ids.add(r.id));
    }

    return Array.from(ids).filter(Boolean);
  };

  componentDidUpdate(prevProps) {
    const loan = this.props.loan;
    const actions = this.props.actions;

    const prevAccountId = prevProps.loan && prevProps.loan.accountId;
    if (loan && (loan.accountId !== prevAccountId || this.props.isAdmin !== prevProps.isAdmin)) {
      this.fetchAccountDevices(loan.accountId);
    }

    // Loan changed → fetch requisition for THIS loan
    if (loan && loan !== prevProps.loan) {
      const rids = this.getRequisitionIdsForLoan();
      actions.psd2ClearAccounts();
      rids.forEach((rid) => {
        return actions.psd2FetchRequisition(rid)
      }); // fetch each requisition
    }

    // When aggregated account ids change → pick first and preload
    if (this.props.psd2AccountIds !== prevProps.psd2AccountIds) {
      const ids = this.props.psd2AccountIds || [];
      if (ids.length > 0) {
        const first = ids[0];
        if (this.state.selectedPsd2AccountId !== first) {
          this.setState({ selectedPsd2AccountId: first });
        }

        // Load IBAN translations for all account IDs (keeps shape: [{accountId, IBAN}])
        actions.psd2FetchIbans({ ids: this.props.psd2AccountIds });

        actions.psd2FetchCategories({});
        actions.psd2FetchCriteria({});
        actions.psd2FetchAccountData(first);
        actions.psd2FetchAllTx({ accountId: first, pageSize: 2000 });
      }
    }
  }



  componentWillUnmount() {
    this.accountDevicesGeneration += 1;
  }

  accountDevicesGeneration = 0;

  fetchAccountDevices = accountId => {
    const generation = ++this.accountDevicesGeneration;
    this.setState({ accountDevices: [], sameInstallAccountsCount: 0 });
    if (!this.props.isAdmin || !accountId) return;
    const isCurrent = () => generation === this.accountDevicesGeneration && this.props.isAdmin;
    const listOf = response => (response.ok && Array.isArray(response.data) ? response.data : null);
    Promise.all([
      accountApi.fetchAccountDevices(accountId).then(listOf).catch(() => null),
      accountApi.fetchSameInstallAccounts(accountId).then(listOf).catch(() => null),
    ]).then(([accountDevices, sameInstallAccounts]) => {
      if (!isCurrent()) return;
      this.setState({
        accountDevices: accountDevices || [],
        sameInstallAccountsCount: sameInstallAccounts ? sameInstallAccounts.length : null,
      });
    });
  };

  // Called by Psd2StatementsAdmin after user confirms the destructive action
  onPurgePsd2ForLoan = () => {
    const requisitionIds = this.getRequisitionIdsForLoan();
    if (!requisitionIds.length) {
      return Promise.resolve(); // nothing to purge
    }

    // Dispatch ducks action; return a resolved promise so the caller can close its modal immediately
    this.props.actions.psd2PurgeData({ requisitionIds });

    // Optimistically clear local PSD2 selection and try to refresh requisition meta
    this.setState({ selectedPsd2AccountId: null });

    const loan = this.props.loan || {};
    const rids =
      loan.psd2RequisitionIds
    if (rids) {
      rids.map((rid) => this.props.actions.psd2FetchRequisition(rid));
    }

    return Promise.resolve();
  };

  // ===== existing actions kept (accept/reject etc.) =====

  onAccept = () => {
    const actions = this.props.actions;
    actions.acceptLoan({ loanId: this.props.loanId });
  };

  onReject = rejectReason => {
    const actions = this.props.actions;
    actions.rejectLoan({ loanId: this.props.loanId, rejectReason });
  };

  onCancelLoan = () => {
    const actions = this.props.actions;
    actions.cancelLoan({ loanId: this.props.loanId });
  };

  getEarlyRepaymentAmount = date => {
    const actions = this.props.actions;
    actions.fetchEarlyRepaymentAmount({
      loanId: this.props.loanId,
      date: date.format('YYYY-MM-DD'),
    });
  };

  shiftInstallments = date => {
    const actions = this.props.actions;
    actions.shiftInstallmentDate({
      loanId: this.props.loanId,
      date,
    });
  };

  // ===== render =====

  render() {
    const t = this.props.t;

    // PSD2 statements page
    if (this.state.showPsd2) {
      const hasAny = (this.props.psd2Transactions && this.props.psd2Transactions.length > 0);

      return (
        <Spin spinning={this.props.isLoading}>
          <div style={{ marginBottom: 12 }}>
            <Button onClick={() => this.setState({ showPsd2: false })}>
              {t('Späť na detail pôžičky')}
            </Button>
          </div>

          <Psd2StatementsAdmin
            loading={this.props.isLoading}
            accountIds={this.props.psd2AccountIds || []}
            selectedAccountId={this.state.selectedPsd2AccountId || undefined}
            accountData={this.props.psd2AccountData}
            ibans={this.props.psd2Ibans || []} // [{ accountId, IBAN }]
            transactions={this.props.psd2Transactions}
            categories={this.props.psd2Categories}
            criteria={this.props.psd2Criteria}
            hasAny={!!hasAny}

            // effects expected by admin component
            fetchAccountData={this.props.actions.psd2FetchAccountData}
            fetchAllTx={this.props.actions.psd2FetchAllTx}
            fetchCategories={this.props.actions.psd2FetchCategories}
            fetchCriteria={this.props.actions.psd2FetchCriteria}
            onChangeAccount={(accountId) => this.setState({ selectedPsd2AccountId: accountId })}

            // destructive hook – uses ducks purge action
            onPurgeTriggered={this.onPurgePsd2ForLoan}
          />
        </Spin>
      );
    }

    // Default: loan detail
    const loan = this.props.loan;

    return (
      <Spin spinning={this.props.isLoading || this.props.isAccepting}>
        {loan ? (
          <div>
            <LoanDetail
              loan={loan}
              loanAllocations={this.props.loanAllocations}
              onAccept={this.onAccept}
              onReject={this.onReject}
              onCancelLoan={this.onCancelLoan}
              isCancelling={this.props.isCancelling}
              token={this.props.token}
              items={this.props.items}
              getEarlyRepaymentAmount={this.getEarlyRepaymentAmount}
              earlyRepayment={this.props.earlyRepayment}
              earlyRepayLoan={this.props.actions.earlyRepayLoan}
              resetEarlyRepayment={this.props.resetEarlyRepayment}
              earlyRepaymentRecord={this.props.earlyRepaymentRecord}
              onSubmitShift={() => window.location.reload()}
              shiftInstallments={this.shiftInstallments}
              // open PSD2 page (only useful if loan has PSD2 requisition)
              onOpenPsd2={() => this.setState({ showPsd2: true })}
              psd2Enabled={parseInt(this.props.usePSD2Integration) === 1}
              accountDevices={this.state.accountDevices}
              sameInstallAccountsCount={this.state.sameInstallAccountsCount}
            />
          </div>
        ) : (
          <div className="spin-empty-content" />
        )}
      </Spin>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/loan-management/containers/LoanDetailContainer.js